import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { useOrganizations, ORGANIZATIONS_PAGE_SIZE as PAGE_SIZE } from "@/hooks/use-organizations";
import { OrganizationDrawer } from "./OrganizationDrawer";
import { OrganizationCreateDialog } from "./OrganizationCreateDialog";
import { OrganizationEditDialog } from "./OrganizationEditDialog";
import { apiClient } from "@/lib/api-client";
import { Building2, Plus, Search, Globe, Phone, Mail, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import type { Organization } from "@/api/generated/client/models";

interface OrganizationsTabProps {
    openDetailId?: number | null;
    onDetailConsumed?: () => void;
}

export function OrganizationsTab({ openDetailId, onDetailConsumed }: OrganizationsTabProps = {}) {
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [page, setPage] = useState(1);
    const [showCreate, setShowCreate] = useState(false);
    const [editOrg, setEditOrg] = useState<Organization | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<Organization | null>(null);
    const [detailOrg, setDetailOrg] = useState<Organization | null>(null);
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Debounce search to avoid a request on every keystroke
    useEffect(() => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 300);
        return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
    }, [search]);

    const { organizations, total, totalPages, isLoading, isPlaceholderData, createOrganization, updateOrganization, deleteOrganization, isCreating, isUpdating } = useOrganizations({ search: debouncedSearch, page });

    // Open detail drawer when navigated from another tab — fetch by ID if not in current page
    useEffect(() => {
        if (openDetailId == null) return;
        const org = organizations.find((o: Organization) => o.id === openDetailId);
        if (org) {
            setDetailOrg(org);
            onDetailConsumed?.();
        } else if (!isLoading) {
            apiClient.organizations.getOrganization({ id: openDetailId }).then((fetched) => {
                setDetailOrg(fetched);
                onDetailConsumed?.();
            }).catch(() => {
                onDetailConsumed?.();
            });
        }
    }, [openDetailId, organizations, isLoading, onDetailConsumed]);

    // Clamp page when total shrinks (e.g. deletion reduces page count)
    useEffect(() => {
        if (totalPages > 0 && page > totalPages) {
            setPage(totalPages);
        }
    }, [totalPages, page]);

    const handleDelete = () => {
        if (!deleteTarget) return;
        deleteOrganization(deleteTarget.id);
        setDeleteTarget(null);
    };

    return (
        <TooltipProvider delayDuration={300}>
            <div className="space-y-4">
                {/* Toolbar */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="relative w-full sm:max-w-xs">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input placeholder="Search organizations..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" />
                    </div>
                    <Button size="sm" className="w-full sm:w-auto" onClick={() => setShowCreate(true)}>
                        <Plus className="h-4 w-4 mr-1.5" />
                        Add Organization
                    </Button>
                </div>

                {/* Content */}
                {isLoading ? (
                    <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}</div>
                ) : organizations.length === 0 ? (
                    <Card className="border-dashed">
                        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                            <div className="rounded-full bg-muted p-4 mb-4">
                                <Building2 className="h-8 w-8 text-muted-foreground" />
                            </div>
                            <h3 className="text-lg font-medium mb-1">No organizations found</h3>
                            <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                                {search ? "Try a different search term." : "Add your first organization to start building your directory."}
                            </p>
                            {!search && (
                                <Button size="sm" onClick={() => setShowCreate(true)}>
                                    <Plus className="h-4 w-4 mr-1.5" />Add Organization
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                ) : (
                    <Card className="overflow-hidden">
                        <Table className="min-w-[760px] md:min-w-0 table-fixed">
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[22%]">Name</TableHead>
                                    <TableHead className="w-[15%]">Type</TableHead>
                                    <TableHead className="w-[18%]">Phone</TableHead>
                                    <TableHead className="w-[22%]">Email</TableHead>
                                    <TableHead className="md:hidden lg:table-cell w-[18%]">Website</TableHead>
                                    <TableHead className="w-[40px]"><span className="sr-only">Actions</span></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {organizations.map((org: Organization) => (
                                    <TableRow key={org.id} tabIndex={0} className="group hover:bg-muted/50 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset" onClick={() => setDetailOrg(org)} onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setDetailOrg(org); } }}>
                                        <TableCell className="font-medium truncate">
                                            {org.name}
                                        </TableCell>
                                        <TableCell className="truncate">
                                            {org.orgType ? org.orgType : <span className="text-muted-foreground/40">—</span>}
                                        </TableCell>
                                        <TableCell className="text-muted-foreground truncate">
                                            {org.phone ? (
                                                <span className="flex items-center gap-1.5 text-sm truncate"><Phone className="h-3.5 w-3.5 shrink-0" />{org.phone}</span>
                                            ) : <span className="text-muted-foreground/40">—</span>}
                                        </TableCell>
                                        <TableCell className="text-muted-foreground truncate">
                                            {org.email ? (
                                                <span className="flex items-center gap-1.5 text-sm truncate"><Mail className="h-3.5 w-3.5 shrink-0" />{org.email}</span>
                                            ) : <span className="text-muted-foreground/40">—</span>}
                                        </TableCell>
                                        <TableCell className="md:hidden lg:table-cell text-muted-foreground truncate">
                                            {org.website ? (
                                                <span className="flex items-center gap-1.5 text-sm truncate"><Globe className="h-3.5 w-3.5 shrink-0" />{org.website}</span>
                                            ) : <span className="text-muted-foreground/40">—</span>}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" aria-label="Delete organization" onClick={(e) => { e.stopPropagation(); setDeleteTarget(org); }}>
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>Delete</TooltipContent>
                                                </Tooltip>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <div className="px-4 py-3 border-t flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">
                                {total} organization{total !== 1 ? "s" : ""}
                                {totalPages > 1 && ` · Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)}`}
                            </span>
                            {totalPages > 1 && (
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1 || isPlaceholderData}>
                                        <ChevronLeft className="h-4 w-4 mr-1" />Previous
                                    </Button>
                                    <span className="text-xs text-muted-foreground px-2">Page {page} of {totalPages}</span>
                                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages || isPlaceholderData}>
                                        Next<ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    </Card>
                )}

                {/* Create Dialog */}
                <OrganizationCreateDialog
                    open={showCreate}
                    onClose={() => setShowCreate(false)}
                    createOrganization={createOrganization}
                    isCreating={isCreating}
                />

                {/* Edit Dialog */}
                <OrganizationEditDialog
                    organization={editOrg}
                    onClose={() => setEditOrg(null)}
                    updateOrganization={updateOrganization}
                    isUpdating={isUpdating}
                />

                {/* Delete Confirmation */}
                <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Delete organization?</AlertDialogTitle>
                            <AlertDialogDescription>
                                This will permanently delete <span className="font-medium text-foreground">{deleteTarget?.name}</span> and unlink it from any claims. Contacts belonging to this organization will not be deleted. This action cannot be undone.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                Delete
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>

            <OrganizationDrawer
                organization={detailOrg}
                open={!!detailOrg}
                onClose={() => setDetailOrg(null)}
                onEdit={(o) => { setDetailOrg(null); setEditOrg(o); }}
            />
        </TooltipProvider>
    );
}